import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Mail, Phone, Globe, MapPin, Pencil, Trash2,
  UserPlus, Plus, Star, Clock, Users, MessageSquare, Calendar,
  Briefcase, Home, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useClient, useContacts, useInteractions, useDeleteClient, useDeleteContact, useUpdateClient } from '@/hooks/useClients';
import {
  CLIENT_TYPE_LABELS, INTERACTION_LABELS, PIPELINE_STAGES, PIPELINE_LABELS,
  type Contact, type Interaction, type InteractionType, type PipelineStage,
} from '@/types/clients';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { StageBadge } from '@/components/clients/StageBadge';
import { ClientFormModal } from '@/components/clients/ClientFormModal';
import { ContactFormModal } from '@/components/clients/ContactFormModal';
import { LogInteractionModal } from '@/components/clients/LogInteractionModal';
import { formatCurrency, initials } from '@/lib/utils';

const INTERACTION_ICONS: Record<InteractionType, typeof Phone> = {
  call:         Phone,
  email:        Mail,
  meeting:      Users,
  site_visit:   MapPin,
  spec_handoff: Briefcase,
  note:         MessageSquare,
};

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'clients', 'write') : false;

  const { data: client, isLoading, error } = useClient(id);
  const { data: contacts } = useContacts(id);
  const { data: interactions } = useInteractions(id);
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();
  const deleteContact = useDeleteContact();

  const [editOpen, setEditOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeleteContact, setConfirmDeleteContact] = useState<Contact | null>(null);

  if (isLoading) return <LoaderScreen />;

  if (error || !client) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load client"
          description={(error as { message?: string })?.message ?? 'Client not found.'}
          action={<Link to="/clients"><Button variant="secondary" leftIcon={<ArrowLeft size={14} />}>Back to Clients</Button></Link>}
        />
      </Card>
    );
  }

  const primaryContact = contacts?.find((c) => c.isPrimary);

  async function onChangeStage(stage: PipelineStage) {
    if (!client || stage === client.stage) return;
    try {
      await updateClient.mutateAsync({ id: client.id, stage });
      toast.success(`Stage → ${PIPELINE_LABELS[stage]}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Failed to update stage.');
    }
  }

  async function onDeleteClient() {
    if (!client) return;
    try {
      await deleteClient.mutateAsync(client.id);
      toast.success(`Deleted ${client.name}`);
      navigate('/clients');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  async function onDeleteContact() {
    if (!confirmDeleteContact || !client) return;
    try {
      await deleteContact.mutateAsync({ id: confirmDeleteContact.id, clientId: client.id });
      toast.success('Contact removed');
      setConfirmDeleteContact(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-8">
      {/* Back link */}
      <Link to="/clients" className="inline-flex items-center gap-2 text-xs text-text-dim hover:text-text-muted transition-colors">
        <ArrowLeft size={12} />
        <span className="kicker">All Clients</span>
      </Link>

      {/* Hero */}
      <header className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-5 min-w-0 flex-1">
          <div className="shrink-0 h-16 w-16 rounded-sm bg-walnut-700 border border-brass-400/30 flex items-center justify-center">
            <Building2 size={24} className="text-brass-300" />
          </div>
          <div className="min-w-0">
            <div className="kicker mb-2">{CLIENT_TYPE_LABELS[client.type]}{client.territory ? ` · ${client.territory}` : ''}</div>
            <h1 className="font-display text-4xl text-text leading-tight tracking-tight mb-3">{client.name}</h1>
            <div className="flex items-center gap-3 flex-wrap">
              <StageBadge stage={client.stage} />
              {client.estimatedAnnualValue ? (
                <Badge tone="brass">{formatCurrency(client.estimatedAnnualValue)} annual</Badge>
              ) : null}
            </div>
          </div>
        </div>

        {canWrite && (
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="secondary" leftIcon={<Pencil size={13} />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Delete client">
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </header>

      {/* Stage progression */}
      {canWrite && (
        <Card>
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <div className="kicker mb-1">Pipeline</div>
              <div className="text-sm text-text">Move this account through the sales cycle.</div>
            </div>
            <div className="w-56">
              <Select
                value={client.stage}
                onChange={(e) => onChangeStage(e.target.value as PipelineStage)}
                options={PIPELINE_STAGES.map((s) => ({ value: s, label: PIPELINE_LABELS[s] }))}
              />
            </div>
          </div>

          {/* Visual progression bar */}
          <div className="flex items-center gap-px mt-4">
            {PIPELINE_STAGES.slice(0, 6).map((s, i) => {
              const currentIndex = PIPELINE_STAGES.indexOf(client.stage);
              const isActive = i <= currentIndex;
              const isCurrent = s === client.stage;
              return (
                <div key={s} className="flex-1">
                  <div className={`h-1.5 ${isActive ? 'bg-brass-400' : 'bg-border'} ${isCurrent ? 'bg-brass-300' : ''}`} />
                  <div className={`mt-2 text-2xs uppercase tracking-ultra ${isActive ? 'text-text-muted' : 'text-text-dim'}`}>
                    {PIPELINE_LABELS[s]}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* 3-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — details */}
        <Card className="lg:col-span-1 space-y-5">
          <div>
            <div className="kicker mb-3">Account Details</div>
            <DetailRow icon={Mail}   label="Email"   value={client.email} link={client.email && `mailto:${client.email}`} />
            <DetailRow icon={Phone}  label="Phone"   value={client.phone} link={client.phone && `tel:${client.phone}`} />
            <DetailRow icon={Globe}  label="Website" value={client.website} link={client.website} external />
            <DetailRow
              icon={Home}
              label="Address"
              value={formatAddress(client.address)}
            />
            {primaryContact && (
              <DetailRow
                icon={Star}
                label="Primary Contact"
                value={`${primaryContact.firstName} ${primaryContact.lastName}`}
              />
            )}
          </div>

          {client.notes && (
            <div className="pt-5 border-t border-border">
              <div className="kicker mb-3">Notes</div>
              <p className="text-sm text-text-muted leading-relaxed whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}

          {client.lastInteractionAt && (
            <div className="pt-5 border-t border-border">
              <div className="kicker mb-2">Last Activity</div>
              <div className="flex items-center gap-2 text-sm text-text">
                <Clock size={13} className="text-text-dim" />
                <span className="tabular">{relativeTime(client.lastInteractionAt)}</span>
              </div>
            </div>
          )}
        </Card>

        {/* Middle — contacts */}
        <Card className="lg:col-span-1 p-0 overflow-hidden">
          <header className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <div className="kicker mb-1">Contacts · {contacts?.length ?? 0}</div>
              <h3 className="font-display text-lg text-text">People</h3>
            </div>
            {canWrite && (
              <Button size="sm" variant="secondary" leftIcon={<UserPlus size={13} />} onClick={() => { setEditingContact(null); setContactOpen(true); }}>
                Add
              </Button>
            )}
          </header>

          {!contacts || contacts.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No contacts yet"
              description="Add the people you work with at this firm."
              action={canWrite && (
                <Button size="sm" leftIcon={<UserPlus size={13} />} onClick={() => { setEditingContact(null); setContactOpen(true); }}>
                  Add Contact
                </Button>
              )}
            />
          ) : (
            <ul className="divide-y divide-border">
              {contacts.map((c) => (
                <li key={c.id} className="px-6 py-4 hover:bg-bg-hover/30 transition-colors group">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 h-9 w-9 rounded-sm bg-walnut-700/60 flex items-center justify-center font-display text-xs text-brass-200">
                      {initials(`${c.firstName} ${c.lastName}`)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text truncate">{c.firstName} {c.lastName}</span>
                        {c.isPrimary && <Star size={11} className="text-brass-300 shrink-0" fill="currentColor" />}
                      </div>
                      {c.title && <div className="text-xs text-text-muted truncate">{c.title}</div>}
                      <div className="mt-1.5 flex items-center gap-3 text-xs">
                        {c.email && <a href={`mailto:${c.email}`} className="text-text-dim hover:text-brass-300 inline-flex items-center gap-1.5"><Mail size={11} />{c.email}</a>}
                        {c.phone && <a href={`tel:${c.phone}`} className="text-text-dim hover:text-brass-300 inline-flex items-center gap-1.5"><Phone size={11} />{c.phone}</a>}
                      </div>
                    </div>
                    {canWrite && (
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => { setEditingContact(c); setContactOpen(true); }}
                          className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm"
                          aria-label="Edit contact"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          onClick={() => setConfirmDeleteContact(c)}
                          className="p-1.5 text-text-dim hover:text-danger hover:bg-danger/10 rounded-sm"
                          aria-label="Delete contact"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Right — interactions */}
        <Card className="lg:col-span-1 p-0 overflow-hidden">
          <header className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <div className="kicker mb-1">Activity · {interactions?.length ?? 0}</div>
              <h3 className="font-display text-lg text-text">Timeline</h3>
            </div>
            {canWrite && (
              <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={() => setLogOpen(true)}>
                Log
              </Button>
            )}
          </header>

          {!interactions || interactions.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="No interactions yet"
              description="Log every call, email, and meeting to keep the team in sync."
              action={canWrite && (
                <Button size="sm" leftIcon={<Plus size={13} />} onClick={() => setLogOpen(true)}>
                  Log Interaction
                </Button>
              )}
            />
          ) : (
            <ol className="relative px-6 py-4">
              {/* Vertical timeline rail */}
              <div className="absolute left-[34px] top-6 bottom-6 w-px bg-border" />

              {interactions.map((it) => (
                <TimelineEntry key={it.id} interaction={it} contacts={contacts ?? []} />
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* Modals */}
      <ClientFormModal open={editOpen} onClose={() => setEditOpen(false)} client={client} />
      <ContactFormModal
        open={contactOpen}
        onClose={() => { setContactOpen(false); setEditingContact(null); }}
        clientId={client.id}
        contact={editingContact}
      />
      <LogInteractionModal open={logOpen} onClose={() => setLogOpen(false)} clientId={client.id} />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDeleteClient}
        loading={deleteClient.isPending}
        destructive
        title={`Delete ${client.name}?`}
        confirmLabel="Delete client"
        description={
          <>
            This permanently removes <span className="text-text">{client.name}</span> along with all{' '}
            {contacts?.length ?? 0} contacts and {interactions?.length ?? 0} interactions. Cannot be undone.
          </>
        }
      />

      <ConfirmDialog
        open={!!confirmDeleteContact}
        onClose={() => setConfirmDeleteContact(null)}
        onConfirm={onDeleteContact}
        loading={deleteContact.isPending}
        destructive
        title={`Remove ${confirmDeleteContact?.firstName} ${confirmDeleteContact?.lastName}?`}
        confirmLabel="Remove contact"
        description="This contact will be removed from the account. Their interaction history is preserved."
      />
    </div>
  );
}

function DetailRow({
  icon: Icon, label, value, link, external,
}: {
  icon: typeof Mail;
  label: string;
  value?: string;
  link?: string | false;
  external?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <Icon size={13} className="text-text-dim shrink-0 mt-1" />
      <div className="flex-1 min-w-0">
        <div className="text-2xs uppercase tracking-ultra text-text-dim mb-0.5">{label}</div>
        {link ? (
          <a
            href={link}
            target={external ? '_blank' : undefined}
            rel={external ? 'noreferrer' : undefined}
            className="text-sm text-text hover:text-brass-300 break-words"
          >
            {value}
          </a>
        ) : (
          <div className="text-sm text-text break-words">{value}</div>
        )}
      </div>
    </div>
  );
}

function TimelineEntry({ interaction, contacts }: { interaction: Interaction; contacts: Contact[] }) {
  const Icon = INTERACTION_ICONS[interaction.type];
  const contact = contacts.find((c) => c.id === interaction.contactId);
  return (
    <li className="relative pl-12 pb-6 last:pb-0">
      <div className="absolute left-2 top-1 h-6 w-6 rounded-full bg-bg-panel border border-border-strong flex items-center justify-center">
        <Icon size={11} className="text-brass-300" />
      </div>
      <div>
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <div className="kicker text-brass-300">{INTERACTION_LABELS[interaction.type]}</div>
          <span className="text-2xs text-text-dim tabular shrink-0">
            {relativeTime(interaction.occurredAt)}
          </span>
        </div>
        <h4 className="text-sm text-text leading-snug mb-1">{interaction.subject}</h4>
        {interaction.body && (
          <p className="text-xs text-text-muted leading-relaxed whitespace-pre-wrap mb-2">
            {interaction.body}
          </p>
        )}
        <div className="flex items-center gap-2 text-2xs text-text-dim">
          {interaction.authorName && <span>by {interaction.authorName}</span>}
          {contact && (
            <>
              {interaction.authorName && <span className="text-border-strong">·</span>}
              <span>with {contact.firstName} {contact.lastName}</span>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function formatAddress(a?: { street1?: string; street2?: string; city?: string; state?: string; postalCode?: string; country?: string }) {
  if (!a) return undefined;
  const parts = [
    a.street1, a.street2,
    [a.city, a.state, a.postalCode].filter(Boolean).join(', '),
    a.country,
  ].filter(Boolean);
  return parts.length ? parts.join('\n') : undefined;
}

function relativeTime(iso: string) {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const min = Math.floor(diffMs / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: days > 365 ? 'numeric' : undefined });
  } catch {
    return iso;
  }
}
